import { httpClient } from './http-client';
import type { ApiResponse } from './api.types';
import type { Cotizacion } from '@/modelos/documentosComerciales/Cotizacion';
import type { DocumentoComercialItem } from '@/modelos/documentosComerciales/DocumentoComercialItem';
import type { DocumentoRelacionadoComercialDto } from '@/modelos/documentosComerciales/DocumentoRelacionadoComercial';

export interface CotizacionItemDto {
  id: string;
  empresaId?: string;
  establecimientoId?: string;
  establecimientoCodigo?: string;
  establecimientoNombre?: string;
  productoId?: string;
  productoCodigo?: string;
  productoNombre: string;
  codigoSunat?: string;
  descripcion?: string;
  unidadMedidaId?: string;
  unidadMedidaNombre?: string;
  unidadMedidaCodigoSunat?: string;
  afectacionTributariaId?: string;
  afectacionTributariaCodigo?: string;
  afectacionTributariaCodigoTributo?: string;
  afectacionTributariaImpuesto?: string;
  afectacionTributariaNombre?: string;
  categoriaId?: string;
  categoriaNombre?: string;
  cantidad: number;
  precioUnitario: number;
  valorVentaUnitario: number;
  precioVentaUnitario: number;
  descuento: number;
  descuentoFactor: number;
  tipoDescuentoFormato?: string;
  valorVentaNeto: number;
  precioVentaNeto: number;
  valorVentaDescontado: number;
  precioVentaDescontado: number;
  subtotal: number;
  igvPorcentaje: number;
  igv: number;
  igvTotal: number;
  total: number;
  orden: number;
  esDetraccion?: boolean;
  detraccionCodigo?: string;
  detraccionPorcentaje?: number;
  usuarioId?: string;
  usuarioNombre?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CotizacionListadoDto {
  id: string;
  empresaId?: string;
  establecimientoId?: string;
  establecimientoCodigo?: string;
  establecimientoNombre?: string;
  serie?: string;
  correlativo?: string;
  numero?: string;
  fechaEmision?: string;
  fechaVencimiento?: string;
  clienteId?: string;
  clienteNumeroDocumento?: string;
  clienteTipoDocumentoCodigo?: string;
  clienteNombres?: string;
  clienteCorreo?: string;
  clienteDireccion?: string;
  vendedorId?: string;
  vendedorNombre?: string;
  monedaCodigo?: string;
  monedaNombre?: string;
  tipoCambio?: number;
  formaPagoId?: string;
  formaPagoNombre?: string;
  condicionesComerciales?: string;
  metodoEnvio?: string;
  fechaEnvioPrevisto?: string;
  esRequiereAprobacion?: boolean;
  ordenCompra?: string;
  centroCosto?: string;
  numeroGuia?: string;
  subtotal?: number;
  montoImponible?: number;
  descuentoTotal?: number;
  valorDescuentoGlobal?: number;
  tipoDescuentoGlobal?: string;
  descuentoItems?: number;
  exonerado?: number;
  inafecto?: number;
  exportacion?: number;
  igv?: number;
  total?: number;
  estado?: string;
  motivoAnulacion?: string;
  motivoCierre?: string;
  observacion?: string;
  observacionInterno?: string;
  usuarioId?: string;
  usuarioNombre?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CotizacionHistorialDto {
  id: string;
  estado: string;
  descripcion?: string;
  usuarioNombre?: string;
  createdAt: string;
}

export interface CotizacionDto extends CotizacionListadoDto {
  items: CotizacionItemDto[];
  historialDocumentos?: CotizacionHistorialDto[];
}

export interface CotizacionItemInputDto {
  id?: string;
  productoId?: string;
  productoCodigo?: string;
  productoNombre: string;
  codigoSunat?: string;
  descripcion?: string;
  unidadMedidaId?: string;
  unidadMedidaNombre?: string;
  unidadMedidaCodigoSunat?: string;
  afectacionTributariaId?: string;
  afectacionTributariaCodigo?: string;
  afectacionTributariaCodigoTributo?: string;
  afectacionTributariaImpuesto?: string;
  afectacionTributariaNombre?: string;
  categoriaId?: string;
  categoriaNombre?: string;
  cantidad: number;
  precioUnitario: number;
  valorVentaUnitario?: number;
  precioVentaUnitario?: number;
  descuento: number;
  descuentoFactor?: number;
  tipoDescuentoFormato?: string;
  valorVentaNeto?: number;
  precioVentaNeto?: number;
  valorVentaDescontado?: number;
  precioVentaDescontado?: number;
  subtotal: number;
  igvPorcentaje: number;
  igv: number;
  igvTotal?: number;
  total: number;
  orden: number;
}

export interface CotizacionInputDto {
  serie?: string;
  fechaEmision: string;
  fechaVencimiento?: string;
  clienteId?: string;
  clienteNumeroDocumento?: string;
  clienteTipoDocumentoCodigo?: string;
  clienteNombres?: string;
  clienteCorreo?: string;
  clienteDireccion?: string;
  vendedorId?: string;
  monedaCodigo: string;
  monedaNombre?: string;
  tipoCambio: number;
  formaPagoId?: string;
  condicionesComerciales?: string;
  metodoEnvio?: string;
  fechaEnvioPrevisto?: string;
  esRequiereAprobacion?: boolean;
  ordenCompra?: string;
  centroCosto?: string;
  numeroGuia?: string;
  subtotal: number;
  montoImponible?: number;
  descuentoTotal: number;
  valorDescuentoGlobal?: number;
  tipoDescuentoGlobal?: string;
  descuentoItems?: number;
  exonerado?: number;
  inafecto?: number;
  exportacion?: number;
  igv: number;
  total: number;
  observacion?: string;
  observacionInterno?: string;
  items: CotizacionItemInputDto[];
}

function dtoAItem(dto: CotizacionItemDto): DocumentoComercialItem {
  return {
    id: dto.id,
    empresaId: dto.empresaId,
    establecimientoId: dto.establecimientoId,
    establecimientoCodigo: dto.establecimientoCodigo,
    establecimientoNombre: dto.establecimientoNombre,
    productoId: dto.productoId,
    productoCodigo: dto.productoCodigo,
    productoNombre: dto.productoNombre,
    codigoSunat: dto.codigoSunat,
    descripcion: dto.descripcion,
    unidadMedidaId: dto.unidadMedidaId,
    unidadMedidaNombre: dto.unidadMedidaNombre,
    unidadMedidaCodigoSunat: dto.unidadMedidaCodigoSunat,
    afectacionTributariaId: dto.afectacionTributariaId,
    afectacionTributariaCodigo: dto.afectacionTributariaCodigo,
    afectacionTributariaCodigoTributo: dto.afectacionTributariaCodigoTributo,
    afectacionTributariaImpuesto: dto.afectacionTributariaImpuesto,
    afectacionTributariaNombre: dto.afectacionTributariaNombre,
    categoriaId: dto.categoriaId,
    categoriaNombre: dto.categoriaNombre,
    cantidad: dto.cantidad,
    precioUnitario: dto.precioUnitario,
    valorVentaUnitario: dto.valorVentaUnitario ?? 0,
    precioVentaUnitario: dto.precioVentaUnitario ?? 0,
    descuento: dto.descuento,
    descuentoFactor: dto.descuentoFactor ?? 0,
    tipoDescuentoFormato: dto.tipoDescuentoFormato,
    valorVentaNeto: dto.valorVentaNeto ?? 0,
    precioVentaNeto: dto.precioVentaNeto ?? 0,
    valorVentaDescontado: dto.valorVentaDescontado ?? 0,
    precioVentaDescontado: dto.precioVentaDescontado ?? 0,
    subtotal: dto.subtotal,
    igvPorcentaje: dto.igvPorcentaje,
    igv: dto.igv,
    igvTotal: dto.igvTotal ?? dto.igv,
    total: dto.total,
    orden: dto.orden,
    esDetraccion: dto.esDetraccion,
    detraccionCodigo: dto.detraccionCodigo,
    detraccionPorcentaje: dto.detraccionPorcentaje,
    usuarioId: dto.usuarioId,
    usuarioNombre: dto.usuarioNombre,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  };
}

export function dtoACotizacion(dto: CotizacionListadoDto): Cotizacion {
  return {
    id: dto.id,
    empresaId: dto.empresaId ?? '',
    establecimientoId: dto.establecimientoId ?? '',
    establecimientoCodigo: dto.establecimientoCodigo,
    establecimientoNombre: dto.establecimientoNombre,
    serie: dto.serie ?? '',
    correlativo: dto.correlativo,
    numero: dto.numero,
    fechaEmision: dto.fechaEmision ?? '',
    fechaVencimiento: dto.fechaVencimiento,
    clienteId: dto.clienteId,
    clienteNumeroDocumento: dto.clienteNumeroDocumento,
    clienteTipoDocumentoCodigo: dto.clienteTipoDocumentoCodigo,
    clienteNombres: dto.clienteNombres,
    clienteCorreo: dto.clienteCorreo,
    clienteDireccion: dto.clienteDireccion,
    vendedorId: dto.vendedorId,
    vendedorNombre: dto.vendedorNombre,
    monedaCodigo: dto.monedaCodigo ?? 'PEN',
    monedaNombre: dto.monedaNombre,
    tipoCambio: dto.tipoCambio ?? 1,
    formaPagoId: dto.formaPagoId,
    formaPagoNombre: dto.formaPagoNombre,
    condicionesComerciales: dto.condicionesComerciales,
    metodoEnvio: dto.metodoEnvio,
    fechaEnvioPrevisto: dto.fechaEnvioPrevisto,
    esRequiereAprobacion: dto.esRequiereAprobacion,
    ordenCompra: dto.ordenCompra,
    centroCosto: dto.centroCosto,
    numeroGuia: dto.numeroGuia,
    subtotal: dto.subtotal ?? 0,
    montoImponible: dto.montoImponible ?? 0,
    descuentoTotal: dto.descuentoTotal ?? 0,
    valorDescuentoGlobal: dto.valorDescuentoGlobal ?? 0,
    tipoDescuentoGlobal: dto.tipoDescuentoGlobal,
    descuentoItems: dto.descuentoItems ?? 0,
    exonerado: dto.exonerado ?? 0,
    inafecto: dto.inafecto ?? 0,
    exportacion: dto.exportacion ?? 0,
    igv: dto.igv ?? 0,
    total: dto.total ?? 0,
    estado: (dto.estado as Cotizacion['estado']) ?? 'borrador',
    motivoAnulacion: dto.motivoAnulacion,
    motivoCierre: dto.motivoCierre,
    observacion: dto.observacion,
    observacionInterno: dto.observacionInterno,
    usuarioId: dto.usuarioId,
    usuarioNombre: dto.usuarioNombre,
    createdAt: dto.createdAt ?? '',
    updatedAt: dto.updatedAt ?? '',
    items: (dto as CotizacionDto).items?.map(dtoAItem),
    historial: (dto as CotizacionDto).historialDocumentos,
  };
}

export const cotizacionesService = {
  getAll: (params?: Record<string, string | number | boolean | undefined>): Promise<ApiResponse<CotizacionListadoDto[]>> =>
    httpClient.get('/cotizaciones', params),

  getById: async (id: string): Promise<ApiResponse<Cotizacion>> => {
    const res = await httpClient.get<CotizacionDto>(`/cotizaciones/${id}`);
    if (!res.ok || !res.data) return { ...res, data: undefined as unknown as Cotizacion };
    return { ...res, data: dtoACotizacion(res.data) };
  },

  create: (data: CotizacionInputDto): Promise<ApiResponse<CotizacionListadoDto>> =>
    httpClient.post('/cotizaciones', data),

  update: (id: string, data: CotizacionInputDto): Promise<ApiResponse<CotizacionListadoDto>> =>
    httpClient.put(`/cotizaciones/${id}`, data),

  generar: (id: string, serie: string): Promise<ApiResponse<CotizacionListadoDto>> =>
    httpClient.patch(`/cotizaciones/${id}/generar`, { serie }),

  aprobar: (id: string, comentario?: string): Promise<ApiResponse<CotizacionListadoDto>> =>
    httpClient.patch(`/cotizaciones/${id}/aprobar`, { comentario }),

  aceptar: (id: string): Promise<ApiResponse<CotizacionListadoDto>> =>
    httpClient.patch(`/cotizaciones/${id}/aceptar`, {}),

  noAprobar: (id: string, motivo?: string): Promise<ApiResponse<CotizacionListadoDto>> =>
    httpClient.patch(`/cotizaciones/${id}/no-aprobar`, { motivo }),

  anular: (id: string, motivo: string): Promise<ApiResponse<CotizacionListadoDto>> =>
    httpClient.patch(`/cotizaciones/${id}/anular`, { motivo }),

  cerrarPerdida: (id: string, motivo: string): Promise<ApiResponse<CotizacionListadoDto>> =>
    httpClient.patch(`/cotizaciones/${id}/cerrar-perdida`, { motivo }),

  eliminar: (id: string): Promise<ApiResponse<void>> =>
    httpClient.delete(`/cotizaciones/${id}`),

  marcarConvertida: (id: string, comprobanteId?: string, comprobanteNumero?: string): Promise<ApiResponse<CotizacionListadoDto>> =>
    httpClient.patch(`/cotizaciones/${id}/marcar-convertida`, comprobanteId ? { comprobanteId, comprobanteNumero } : {}),

  getRelacionados: (id: string): Promise<ApiResponse<DocumentoRelacionadoComercialDto[]>> =>
    httpClient.get(`/cotizaciones/${id}/relacionados`),

  restaurarAceptada: (id: string): Promise<ApiResponse<CotizacionListadoDto>> =>
    httpClient.patch(`/cotizaciones/${id}/restaurar-aceptada`, {}),

  revisarVencimientos: (): Promise<ApiResponse<number>> =>
    httpClient.post('/cotizaciones/revisar-vencimientos', {}),

  downloadPdf: (id: string): Promise<Blob> =>
    httpClient.obtenerBlob(`/cotizaciones/${id}/pdf`),

  enviarCorreo: (id: string, email: string, verUrl: string): Promise<ApiResponse<void>> =>
    httpClient.post(`/cotizaciones/${id}/enviar-correo`, { email, verUrl }),

  downloadExcel: (params?: {
    empresaId?: string;
    establecimientoId?: string;
    search?: string;
    estado?: string;
    fechaDesde?: string;
    fechaHasta?: string;
    vendedor?: string;
    moneda?: string;
    totalMin?: number;
    totalMax?: number;
    ids?: string[];
  }): Promise<Blob> => {
    const { ids, ...rest } = params ?? {};
    const query: Record<string, string | number | boolean | undefined> = rest as Record<string, string | number | boolean | undefined>;
    if (ids && ids.length > 0) query.ids = ids.join(',');
    return httpClient.obtenerBlob('/reportes/cotizaciones/excel', query);
  },
};
